/*
    Copyright (c) Computer-L.A.N. GmbH (http://www.computer-lan.de).
    Alle Rechte vorbehalten.
*/

import React from "react";
import { Autocomplete, Button, debounce, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField } from "@mui/material";
import { v4 } from "uuid";
import DialogBase from "../../../../Basis/Components/DialogBase";
import DialogContentFieldset from "../../../../Basis/Components/DialogContentFieldset";
import DialogContentFieldsetGroup from "../../../../Basis/Components/DialogContentFieldsetGroup";
import withTooltip from "../../../../Basis/Components/HOC/withTooltip";
import LoadingButton from "../../../../Basis/Components/LoadingButton";
import AnyUtil from "../../../../Basis/Utils/AnyUtil";
import useAuswahl from "../../../../Basis/Utils/useAuswahl";
import { useAppNotificationContext } from "../../../Benachrichtigungen/Components/AppNotificationContext";
import CommandsFeld from "../../../DomainDrivenDesign/Commands/Feld";
import Filter from "../../../DomainDrivenDesign/Commands/Filter";
import FilterInfo from "../../../DomainDrivenDesign/Queries/FilterInfo";
import Feld from "../../../Exporte/Queries/Feld";
import { translationKeys, useTranslation } from "../../Internationalisierung/i18nExporte";
import { Exportvorlage } from "../../Queries/FindeExportvorlagen";
import { ExportvorlageFeld, ExportvorlageFilter, PromiseLadeExportvorlage } from "../../Queries/LadeExportvorlage";
import SpaltenUndFilterView, { TABS, TabValue } from "../SpaltenUndFilterView";

type FeldMitId = Feld & { id: string };

export type ErstelleCsvExportInput = {
    filter: Filter[],
    felder: CommandsFeld[],
};

type FindeExportvorlagen = (suchbegriff: string) => Promise<Exportvorlage[]>;

export interface ErstelleCsvExportDialogProps {
    isOpen: boolean;
    onAbgebrochen: () => void;
    onExportiert: () => void;
    felder: Feld[];
    findeFilterInfosFuerCsvExport: (suchbegriff: string) => Promise<FilterInfo[]>;
    erstelleCsvExport: (input: ErstelleCsvExportInput) => Promise<void>;
    findeExportvorlagen?: FindeExportvorlagen;
    dialogTitle: string;
}

const SCHRITTE = {
    VORLAGE: "VORLAGE",
    EXPORT: "EXPORT",
} as const;

type Schritt = keyof typeof SCHRITTE;

interface ExportvorlagenAutocompleteProps {
    findeExportvorlagen: FindeExportvorlagen;
    value: string | null;
    onChange: (exportvorlageId: string | null) => void;
    caption?: string;
    autoFocus?: boolean;
    placeholder?: string;
}

const WAIT_MILLIS = 500;

const ExportvorlagenAutocomplete = withTooltip(function (props: ExportvorlagenAutocompleteProps) {
    const [options, setOptions] = React.useState<Exportvorlage[]>([]);
    const [inputValue, setInputValue] = React.useState("");
    const [suchbegriff, setSuchbegriff] = React.useState("");
    const debouncedSetSuchbegriff = React.useMemo(() => debounce(setSuchbegriff, WAIT_MILLIS), [setSuchbegriff]);
    const [open, setOpen] = React.useState(false);

    const ladeExportvorlage = new PromiseLadeExportvorlage();
    const firstRender = React.useRef(true);
    React.useEffect(() => {
        if (!firstRender.current) {
            return;
        }

        firstRender.current = false;
        let mounted = true;
        if (props.value) {
            ladeExportvorlage.execute({ exportvorlageId: props.value }).then(exportvorlage => {
                if (mounted) {
                    setInputValue(exportvorlage.name);
                }
            });
        }

        return () => {
            mounted = false;
        };
    }, [props.value]);

    React.useEffect(() => {
        if (!open) {
            return;
        }

        let mounted = true;
        if (!props.findeExportvorlagen) {
            return;
        }

        props.findeExportvorlagen(suchbegriff).then(exporVorlagen => {
            if (mounted) {
                setOptions(exporVorlagen);
            }
        });

        return () => {
            mounted = false;
        };
    }, [suchbegriff, open]);

    React.useEffect(() => {
        debouncedSetSuchbegriff(inputValue);
        return () => {
            debouncedSetSuchbegriff.clear();
        };
    }, [inputValue, debouncedSetSuchbegriff]);

    function exportvorlage(exportvorlageId: string | null): Exportvorlage | null {
        if (exportvorlageId === null) return null;

        const idx = options.findIndex(e => e.exportvorlageId === exportvorlageId);
        if (idx === -1) return null;
        return options[idx];
    }

    return (
        <Autocomplete<Exportvorlage | null>
            autoSelect
            open={open}
            onOpen={setOpen.bind(null, true)}
            onClose={setOpen.bind(null, false)}
            options={options}
            getOptionLabel={option => option?.name ?? ""}
            value={exportvorlage(props.value)}
            onChange={(_, newValue) => {
                if (newValue) props.onChange(newValue.exportvorlageId);
                else props.onChange(null);
            }}
            inputValue={inputValue}
            onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
            renderInput={params => (
                <TextField
                    {...params}
                    autoFocus={props.autoFocus}
                    label={props.caption}
                    placeholder={props.placeholder}
                    InputLabelProps={{ shrink: true }}
                />
            )}
        />
    );
});

export default function ErstelleCsvExportDialog(props: ErstelleCsvExportDialogProps) {
    const { t } = useTranslation();
    const hatExportvorlagen = !!props.findeExportvorlagen;
    const initialSchritt = hatExportvorlagen ? SCHRITTE.VORLAGE : SCHRITTE.EXPORT;
    const [schritt, setSchritt] = React.useState<Schritt>(initialSchritt);
    const [tabValue, setTabValue] = React.useState<TabValue>(TABS.SPALTEN);
    const appNotificationContext = useAppNotificationContext();

    const [filter, setFilter] = React.useState<(Filter | ExportvorlageFilter)[]>([]);

    const [felder, setFelder] = React.useState<(FeldMitId | ExportvorlageFeld)[]>([]);
    React.useEffect(() => {
        setFelder(props.felder.map(feld => ({ ...feld, id: v4() })));
    }, [props.felder.length]);

    const auswahl = useAuswahl(felder, feld => "exportvorlageFeldId" in feld ? feld.exportvorlageFeldId : feld.id);
    const [exportvorlageId, setExportvorlageId] = React.useState<string | null>(null);

    async function handleExportierenClick() {
        setLoading(true);
        try {
            await props.erstelleCsvExport({
                felder: auswahl.ausgewaehlteElemente.map(f => ({
                    name: f.name,
                    zusatzfeldDefinitionId: f.zusatzfeldDefinitionId,
                })),
                filter: filter
                    .filter(f => {
                        if (f.wert.typ === "ENUM") return !AnyUtil.isNullOrUndefined(f.wert.als.enum);
                        else if (f.wert.typ === "STRING") return !AnyUtil.isNullOrUndefined(f.wert.als.string);
                        else if (f.wert.typ === "BOOLEAN") return !AnyUtil.isNullOrUndefined(f.wert.als.boolean);
                        else if (f.wert.typ === "STRINGARRAY") return !AnyUtil.isNullOrUndefined(f.wert.als.stringArray);
                        else if (f.wert.typ === "INTEGER") return !AnyUtil.isNullOrUndefined(f.wert.als.integer);
                        else if (f.wert.typ === "DECIMAL") return !AnyUtil.isNullOrUndefined(f.wert.als.decimal);
                        else if (f.wert.typ === "DATETIME") return !AnyUtil.isNullOrUndefined(f.wert.als.dateTime);
                        else if (f.wert.typ === "GUID") return !AnyUtil.isNullOrUndefined(f.wert.als.guid);
                        return false;
                    })
                    .map(f => ({
                        feld: f.feld,
                        zusatzfeldDefinitionId: f.zusatzfeldDefinitionId,
                        operator: f.operator,
                        wert: f.wert
                    })),
            });

            appNotificationContext.holeBenachrichtigungen();
            props.onExportiert();
        } finally {
            setLoading(false);
        }
    }

    function handleSortieren(drag: (FeldMitId | ExportvorlageFeld), drop: (FeldMitId | ExportvorlageFeld)) {
        setFelder(prev => {
            if (prev.length < 2) {
                return prev;
            }

            const dragIndex = prev.indexOf(drag);
            if (dragIndex === -1) {
                return prev;
            }

            const dropIndex = prev.indexOf(drop);
            if (dropIndex === -1) {
                return prev;
            }

            if (dragIndex === dropIndex) {
                return prev;
            }

            const tmp = [...prev];
            tmp.splice(dragIndex, 1);
            tmp.splice(dropIndex, 0, drag)
            return tmp;
        });
    }

    function wenn(s: Schritt, node: React.ReactNode) {
        return s === schritt && node;
    }

    const [loading, setLoading] = React.useState(false);
    const ladeExportvorlage = new PromiseLadeExportvorlage();
    function handleWeiterClick() {
        if (exportvorlageId) {
            setLoading(true);
            ladeExportvorlage
                .execute({ exportvorlageId })
                .then(exportvorlage => {
                    setFelder(exportvorlage.felder);
                    setFilter(exportvorlage.filter);
                    setLoading(false);
                    setSchritt(SCHRITTE.EXPORT);
                })
                .finally(() => {
                    setLoading(false);
                });
        } else {
            setSchritt(SCHRITTE.EXPORT);
        }
    }

    React.useEffect(() => {
        if (schritt === "EXPORT") {
            if (exportvorlageId === null) {
                auswahl.aendereAuswahlFuerAlle(true);
            } else {
                felder.forEach(feld => {
                    if ("istZuExportieren" in feld) {
                        auswahl.aendereAuswahl(feld, feld.istZuExportieren);
                    }
                });
            }
        }
    }, [schritt, exportvorlageId]);

    function handleZurueckClick() {
        setSchritt(SCHRITTE.VORLAGE);
        setExportvorlageId(null);
        setFelder(props.felder.map(feld => ({ ...feld, id: v4() })));
    }

    function setzeDialogZurueck() {
        setFilter([]);
        setTabValue(TABS.SPALTEN);
        setSchritt(initialSchritt);
        setExportvorlageId(null);
        if (!hatExportvorlagen) {
            auswahl.aendereAuswahlFuerAlle(true);
        }
    }

    const fullScreen = schritt === SCHRITTE.EXPORT;
    const maxWidth = schritt === SCHRITTE.EXPORT ? "lg" : "md";
    const weiterCaption = t(
        exportvorlageId
            ? translationKeys.erstelleCsvExportDialog.weiter
            : translationKeys.erstelleCsvExportDialog.ohneVorlageWeiter);
    return (
        <DialogBase fullScreen={fullScreen} open={props.isOpen} maxWidth={maxWidth} scroll="paper">
            <DialogTitle>
                {props.dialogTitle}
            </DialogTitle>
            <DialogContent>
                {!!props.findeExportvorlagen && wenn(SCHRITTE.VORLAGE, (
                    <>
                        <DialogContentText>
                            {t(translationKeys.erstelleCsvExportDialog.dialogText)}
                        </DialogContentText>
                        <DialogContentFieldsetGroup>
                            <DialogContentFieldset>
                                <ExportvorlagenAutocomplete
                                    autoFocus
                                    caption={t(translationKeys.erstelleCsvExportDialog.exportvorlage)}
                                    placeholder={t(translationKeys.erstelleCsvExportDialog.keineVorlage)}
                                    findeExportvorlagen={props.findeExportvorlagen}
                                    value={exportvorlageId}
                                    onChange={setExportvorlageId}
                                />
                            </DialogContentFieldset>
                        </DialogContentFieldsetGroup>
                    </>
                ))}

                {wenn(SCHRITTE.EXPORT, (
                    <SpaltenUndFilterView<(FeldMitId | ExportvorlageFeld), (Filter | ExportvorlageFilter)>
                        tabValue={tabValue}
                        setTabValue={setTabValue}
                        felder={felder}
                        onSortieren={handleSortieren}
                        auswahl={auswahl}
                        filter={filter}
                        setFilter={setFilter}
                        findeFilterInfosFuerCsvExport={props.findeFilterInfosFuerCsvExport}
                        commandName={props.erstelleCsvExport.name}
                        sortieren
                    />
                ))}
            </DialogContent>
            <DialogActions>
                {hatExportvorlagen && wenn(SCHRITTE.EXPORT, (
                    <Button onClick={handleZurueckClick} sx={{ marginRight: "auto" }}>
                        {t(translationKeys.erstelleCsvExportDialog.zurueck)}
                    </Button>
                ))}
                <Button
                    onClick={() => {
                        props.onAbgebrochen();

                        // Das setTimeout sorgt dafür, dass es keine Verzögerung beim Schließen des Dialogs gibt
                        window.setTimeout(setzeDialogZurueck, 0);
                    }}
                >
                    {t(translationKeys.erstelleCsvExportDialog.abbrechen)}
                </Button>

                {wenn(SCHRITTE.VORLAGE, (
                    <LoadingButton loading={loading} onClick={handleWeiterClick}>
                        {weiterCaption}
                    </LoadingButton>
                ))}

                {wenn(SCHRITTE.EXPORT, (
                    <LoadingButton loading={loading} disabled={!auswahl.ausgewaehlteElemente.length} onClick={handleExportierenClick}>
                        {t(translationKeys.erstelleCsvExportDialog.exportieren)}
                    </LoadingButton>
                ))}
            </DialogActions>
        </DialogBase>
    );
}
